import Component from '@glimmer/component'
import { service } from '@ember/service'
import { LinkTo } from '@ember/routing'
import { t } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import WidgetCard, { WidgetContent } from 'teacher-ui-client/components/teacher-home/widget-card'
import { ThemeKeys, subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import {
  QUICK_ACTIONS_BY_SUBSCRIPTION_TYPE,
  type QuickAction,
} from 'teacher-ui-client/components/teacher-home/widgets/quick-actions/data'
import { teacherEventClick } from 'teacher-ui-client/modifiers/teacher-event'
import { concat } from '@ember/helper'

interface Signature {
  Element: HTMLElement
}

interface ThemeClasses {
  iconBg: string
  iconText: string
}

const THEME_CLASSES: Partial<Record<ThemeKeys, ThemeClasses>> = {
  [ThemeKeys.blue]: {
    iconBg: 'bg-oceany-blue-50',
    iconText: 'text-oceany-blue-300',
  },
  [ThemeKeys.darkBlue]: {
    iconBg: 'bg-wl-blue-50',
    iconText: 'text-wl-blue-300',
  },
  [ThemeKeys.green]: {
    iconBg: 'bg-ms-green-50',
    iconText: 'text-ms-green-300',
  },
}

const DEFAULT_THEME: ThemeClasses = {
  iconBg: 'bg-dusty-black-50',
  iconText: 'text-dusty-black-300',
}

export class TeacherHomeQuickActions extends Component<Signature> {
  @service declare subscriptionType: SubscriptionTypeService

  get actions(): QuickAction[] {
    return QUICK_ACTIONS_BY_SUBSCRIPTION_TYPE[this.subscriptionType.current] ?? []
  }

  get theme(): ThemeClasses {
    const key = subscriptionTypeToThemeKey(this.subscriptionType.current)
    return THEME_CLASSES[key] ?? DEFAULT_THEME
  }

  <template>
    <WidgetCard
      @title={{t "teacherHome.widgets.quickActions.title"}}
      data-test-teacher-home-widget-card="quick-actions"
      ...attributes
    >
      <WidgetContent>
        <ul class="divide-dusty-black-100 flex flex-1 flex-col divide-y">
          {{#each this.actions as |quickAction|}}
            <li>
              <LinkTo
                {{teacherEventClick
                  (concat "TeacherHome:quick_action_clicked:" quickAction.id)
                  this.subscriptionType.current
                }}
                data-test-quick-action={{quickAction.id}}
                @route={{quickAction.route}}
                @models={{quickAction.models}}
                class="group flex items-center gap-3 px-1 py-2 no-underline transition-colors hover:bg-neutral-50/70"
              >
                <span
                  class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md
                    {{this.theme.iconBg}}
                    {{this.theme.iconText}}"
                  aria-hidden="true"
                >
                  <PhosphorIcon @icon={{quickAction.icon}} class="text-lg" />
                </span>
                <span class="text-dusty-black-300 flex-1 text-sm font-semibold">
                  {{t quickAction.labelKey}}
                </span>
                <PhosphorIcon @icon="phosphor-caret-right-bold" class="text-dusty-black-300 h-5 w-5 text-lg" />
              </LinkTo>
            </li>
          {{/each}}
        </ul>
      </WidgetContent>
    </WidgetCard>
  </template>
}

export default TeacherHomeQuickActions
